import { FocusedLinksBlockProps } from "/Models/Generated/FocusedLinksBlockProps";
import "./FocusedLinksBlock.scss";
import {
  Link,
  ArrowRightIcon,
  IconLookup,
  ArrowLineUpRightIcon,
  DownloadSimpleIcon,
  OptimizelyEditProperty,
  CaretDownIcon,
} from "/App.Components";
import { ColorTheme } from "/Models/Models/ColorTheme";
import { LinkTargetType } from "/Models/Generated/LinkTargetType";
import { useState } from "react";
import { createSpreadableBem } from "/Helpers/createBem";

interface Props extends FocusedLinksBlockProps {
  isExpandable?: boolean;
}

const FocusedLinksBlock = ({
  heading,
  headingPropertyName,
  icon,
  introText,
  introTextPropertyName,
  links,
  linksPropertyName,
  specialLinks,
  isExpandable,
}: Props) => {
  const GetIcon = (linkTargetType: LinkTargetType) => {
    if (linkTargetType === LinkTargetType.External) return ArrowLineUpRightIcon;
    if (linkTargetType === LinkTargetType.File) return DownloadSimpleIcon;
    return ArrowRightIcon;
  };
  const [isOpen, setIsOpen] = useState(false);

  const bem = createSpreadableBem("focused-links-block");
  return (
    <aside {...bem("")}>
      <div
        {...bem("__heading-wrapper", {
          "--no-icon": !icon || icon === "None",
        })}
      >
        {icon && (
          <div>
            <IconLookup
              iconType={icon}
              iconProps={{
                thickness: "Light",
                size: "32",
                color: ColorTheme.black,
              }}
            />
          </div>
        )}
        <OptimizelyEditProperty propertyName={headingPropertyName}>
          {heading && <h2 {...bem("__heading")}>{heading}</h2>}
        </OptimizelyEditProperty>
      </div>
      <div
        {...bem("__content-wrapper", {
          "--expandable": isExpandable,
        })}
      >
        {isExpandable && (
          <button
            {...bem("__expandable-button", {
              "--open": isOpen,
            })}
            onClick={() => setIsOpen((prev) => !prev)}
          >
            <CaretDownIcon size="24" thickness="Reg" />
          </button>
        )}
        <div
          {...bem("__expandable-container", {
            "--open": !isExpandable || isOpen,
          })}
        >
          <OptimizelyEditProperty propertyName={introTextPropertyName}>
            {introText && <p {...bem("__intro-text")}>{introText}</p>}
          </OptimizelyEditProperty>
          <OptimizelyEditProperty propertyName={linksPropertyName} clientRender>
            {(links?.length || specialLinks?.length) && (
              <ul {...bem("__list")}>
                {(links ?? []).concat(specialLinks ?? []).map((link, idx) => (
                  <li key={idx} {...bem("__list-item")}>
                    <Link
                      {...link}
                      isExternal={
                        link.linkTargetType === LinkTargetType.External
                      }
                      target={
                        link.openInNewTab ||
                        link.linkTargetType !== LinkTargetType.Page
                          ? "_blank"
                          : "_self"
                      }
                      iconPlacement="before"
                      colorTheme={ColorTheme.default}
                      icon={GetIcon(link.linkTargetType)}
                      size="x-small"
                      fixedIcon={true}
                      download={link.linkTargetType === LinkTargetType.File}
                    />
                  </li>
                ))}
              </ul>
            )}
          </OptimizelyEditProperty>
        </div>
      </div>
    </aside>
  );
};

export default FocusedLinksBlock;
